import { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MessageCircle, Send } from "lucide-react";
import Header from "../component/header";
import Footer from "../component/Footer";
import { UserDataContext } from "../context/User";
import Breadcrumb from "../component/Breadcrumb";
import BannerImg from "../image/comunity.png";
import Banner from "../component/Banner";

function authorName(author) {
  if (!author) return "LWBL Member";
  const fullName = [author.firstName, author.lastName].filter(Boolean).join(" ");
  return author.preferredName || fullName || author.memberId || "LWBL Member";
}

function formatDate(value) {
  if (!value) return "Recently";
  return new Date(value).toLocaleString();
}

export default function DiscussionDetail() {
  const { id } = useParams();
  const { userData } = useContext(UserDataContext);
  const [discussion, setDiscussion] = useState(null);
  const [replies, setReplies] = useState([]);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState({ type: "", message: "" });

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`/api/discussions/${id}`)
      .then(({ data }) => {
        if (!mounted || !data.success) return;
        setDiscussion(data.discussion);
        setReplies(data.replies || []);
      })
      .catch(() => {
        if (mounted) setNotice({ type: "error", message: "Discussion could not be loaded." });
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [id]);

  const submitReply = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setNotice({ type: "", message: "" });

    try {
      const { data } = await axios.post(`/api/discussions/${id}/replies`, { content: reply }, { withCredentials: true });
      if (data.success && data.reply) {
        setReplies((current) => [...current, data.reply]);
        setDiscussion((current) => current ? { ...current, replyCount: data.replyCount } : current);
        setReply("");
      }
    } catch (error) {
      setNotice({
        type: "error",
        message: error?.response?.data?.message || "Reply could not be added.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <Banner
        backgroundImage={BannerImg}
        heading="Discussion"
        paragraph="Continue the conversation with thoughtful replies, practical questions, and constructive support from the LWBL community."
        minHeight="70vh"
        padding="80px 0"
        bton="Members please sign-in"
      />
      <Breadcrumb items={[{ label: "Discussions", path: "/discussions" }, { label: discussion?.title || "Discussion" }]} />
      <main className="bg-[#F9FAFB] px-4 py-12">
        <div className="mx-auto max-w-4xl">
          <Link to="/discussions" className="mb-6 inline-flex items-center gap-2 font-outfit text-sm font-semibold text-cyan-700">
            <ArrowLeft size={16} />
            Back to discussions
          </Link>

          {notice.message && (
            <div className={`mb-5 rounded-2xl border px-4 py-3 font-outfit text-sm ${
              notice.type === "error" ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"
            }`}>
              {notice.message}
            </div>
          )}

          {loading ? (
            <div className="rounded-2xl border border-gray-100 bg-white p-8 text-center font-outfit text-sm text-gray-500">
              Loading discussion...
            </div>
          ) : discussion ? (
            <>
              <article className="rounded-3xl border border-gray-100 bg-white p-7 shadow-sm">
                <span className="rounded-full bg-cyan-50 px-3 py-1 font-outfit text-xs font-semibold uppercase tracking-[0.16em] text-cyan-700">
                  {discussion.category}
                </span>
                <h1 className="mt-4 font-cinzel text-4xl leading-tight text-gray-950">{discussion.title}</h1>
                <p className="mt-3 font-outfit text-sm text-gray-500">
                  Started by {authorName(discussion.author)} - {formatDate(discussion.createdAt)}
                </p>
                <p className="mt-6 whitespace-pre-wrap font-outfit text-base leading-8 text-gray-700">
                  {discussion.content}
                </p>
              </article>

              <section className="mt-8">
                <div className="mb-4 flex items-center gap-2">
                  <MessageCircle size={20} className="text-cyan-700" />
                  <h2 className="font-cinzel text-2xl text-gray-950">Replies ({discussion.replyCount || replies.length})</h2>
                </div>
                <div className="space-y-4">
                  {replies.length ? replies.map((item) => (
                    <div key={item.id} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <p className="font-outfit text-sm font-semibold text-gray-950">{authorName(item.author)}</p>
                        <p className="font-outfit text-xs text-gray-400">{formatDate(item.createdAt)}</p>
                      </div>
                      <p className="whitespace-pre-wrap font-outfit text-sm leading-7 text-gray-700">{item.content}</p>
                    </div>
                  )) : (
                    <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-6 text-center font-outfit text-sm text-gray-500">
                      No replies yet. Be the first to respond.
                    </div>
                  )}
                </div>
              </section>

              <form onSubmit={submitReply} className="mt-8 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                <label className="font-outfit text-sm font-semibold text-gray-700">Add a constructive reply</label>
                <textarea
                  value={reply}
                  onChange={(event) => setReply(event.target.value)}
                  rows={5}
                  maxLength={1500}
                  placeholder="Share encouragement, ask a useful question, or offer a practical suggestion."
                  className="mt-2 w-full resize-none rounded-xl border border-gray-200 px-4 py-3 font-outfit text-sm outline-none focus:border-cyan-400"
                  required
                />
                <button
                  type="submit"
                  disabled={submitting || !userData}
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-gray-900 px-5 py-3 font-outfit text-sm font-semibold text-white hover:bg-cyan-600 disabled:opacity-60"
                >
                  <Send size={16} />
                  {submitting ? "Posting..." : "Post Reply"}
                </button>
              </form>
            </>
          ) : null}
        </div>
      </main>
      <Footer />
    </>
  );
}
